import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as lodash from 'lodash';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type {
	BlueprintDistributionCenterLink,
	FullLocation,
	GetDistributionCentersByDssiCodesArgs,
	ReportRequestRow,
	RequestOrderGuideReportArgs,
	RequestStockMatrixReportArgs,
	SelectBlueprintsDataMultSuppliersArgs,
	SelectMasterOrderGuidesDataResult_MasterOrderGuide,
	Supplier,
} from '~/ogm-hub';
import {
	LocationDistributionCenterController,
	ProductController,
	ReportController,
} from '~/ogm-hub';
import type { IAppState } from '~/appstate';
import type {
	FetchBlueprintDcLinksForCustomerAndSuppliersArgs,
	fetchDssiCodesForCustomerAndSupplierArgs,
} from '../DistributionCentersSlice';
import {
	fetchBlueprintDcLinksForCustomerAndSupplier,
	fetchDistributionCentersForDssiCodes,
	fetchDssiCodesForCustomerAndSupplier,
	selectDistributionCentersByDssiCodes,
} from '../DistributionCentersSlice';
import { fetchSuppliersForCustomer } from '../SuppliersSlice';
import type { IDistributionCenter } from '../schema';

export interface SupplierInfo extends Supplier {
	distributionCenterCodes: string[];
}

export interface FetchDistributionCentersWithBlueprintsArgs {
	suppliers: SupplierInfo[];
	blueprints: SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
}

export interface FetchBlueprintsWithDistributionCentersArgs {
	suppliers: SupplierInfo[];
	distributionCenters: IDistributionCenter[];
}

export interface IReportRequestModalState {
	loadingStates: {
		loadingDistributionCenters: boolean;
		loadingMasterOrderGuides: boolean;
		loadingBlueprintDcLinks: boolean;
		loadingLocations: boolean;
		loadingSuppliers: boolean;
		submittingRequest: boolean;
		submitSuccess: boolean;
	};
	data: {
		distributionCenters: IDistributionCenter[];
		suppliers: SupplierInfo[];
		masterOrderGuides: SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
		locations: FullLocation[];
		blueprintDcLinks: BlueprintDistributionCenterLink[];
	};
	filteredData: {
		distributionCenters: IDistributionCenter[];
		masterOrderGuides: SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
	};
	selections: {
		distributionCenters: IDistributionCenter[];
		suppliers: SupplierInfo[];
		masterOrderGuides: SelectMasterOrderGuidesDataResult_MasterOrderGuide[];
		locations: FullLocation[];
		splitReportByDc: boolean;
	};
	errors: {
		fetchDataError: boolean;
		requestReportError: boolean;
	};
	customerId: number;
}

const initialState: IReportRequestModalState = {
	loadingStates: {
		loadingDistributionCenters: false,
		loadingMasterOrderGuides: false,
		loadingBlueprintDcLinks: false,
		loadingLocations: false,
		loadingSuppliers: false,
		submittingRequest: false,
		submitSuccess: false,
	},
	data: {
		distributionCenters: [],
		suppliers: [],
		masterOrderGuides: [],
		locations: [],
		blueprintDcLinks: [],
	},
	filteredData: {
		distributionCenters: [],
		masterOrderGuides: [],
	},
	selections: {
		distributionCenters: [],
		suppliers: [],
		masterOrderGuides: [],
		locations: [],
		splitReportByDc: true,
	},
	errors: {
		fetchDataError: false,
		requestReportError: false,
	},
	customerId: null,
};

export const loadModalForCustomer = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportModalSlice/loadModalForCustomer',
	async (customerId: number, { dispatch }) => {
		dispatch(reportModalSlice.actions.setCurrentCustomerId(customerId));

		const suppliersResponse = await dispatch(
			fetchSuppliersForCustomer(customerId)
		);

		if (fetchSuppliersForCustomer.fulfilled.match(suppliersResponse)) {
			const suppliers = lodash.sortBy(suppliersResponse.payload, (sup) =>
				sup.name.toLowerCase()
			);
			const supplierInfoItems = suppliers.map((supplier) => {
				return {
					...supplier,
					distributionCenterCodes: [],
					blueprintDcLinks: [],
				};
			});
			dispatch(reportModalSlice.actions.setSupplierData(supplierInfoItems));
			if (supplierInfoItems.length === 1) {
				dispatch(selectSupplier(supplierInfoItems[0]));
			}
		}
	}
);

export const selectSupplier = createAsyncThunk<
	void,
	SupplierInfo,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportModalSlice/selectSupplier',
	async (supplier: SupplierInfo, { getState, dispatch }) => {
		const state = getState();
		const currentSupplier =
			state.reportsPage.reportRequestModalState.selections.suppliers[0];

		if (currentSupplier?.id !== supplier.id) {
			dispatch(reportModalSlice.actions.setSupplier([supplier]));
			dispatch(fetchBlueprintDcLinks([supplier]));
			dispatch(fetchDistributionCenters([supplier]));
			dispatch(fetchMasterOrderGuides([supplier]));
			dispatch(fetchLocations([supplier]));
		}
	}
);

export const selectSuppliers = createAsyncThunk<
	void,
	SupplierInfo[],
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportModalSlice/selectSupplier',
	async (suppliers: SupplierInfo[], { getState, dispatch }) => {
		if (suppliers == null) {
			dispatch(reportModalSlice.actions.resetModal());
		} else {
			const state = getState();
			const currentSuppliers =
				state.reportsPage.reportRequestModalState.selections.suppliers
					.map((x) => x.id)
					.sort();

			if (currentSuppliers !== suppliers.map((x) => x.id).sort()) {
				dispatch(reportModalSlice.actions.setSupplier(suppliers));
				dispatch(fetchBlueprintDcLinks(suppliers));
				dispatch(fetchDistributionCenters(suppliers));
				dispatch(fetchMasterOrderGuides(suppliers));
				dispatch(fetchLocations(suppliers));
			}
		}
	}
);

export const fetchDistributionCenters = createAsyncThunk<
	void,
	SupplierInfo[],
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportModalSlice/fetchDistributionCenters',
	async (
		suppliers: SupplierInfo[],
		{ getState, dispatch, rejectWithValue }
	) => {
		const state = getState();
		const customerId = state.reportsPage.reportRequestModalState.customerId;
		let distributionCenterCodes: string[] = [];

		for (const supplier of suppliers) {
			if (supplier.distributionCenterCodes.length === 0) {
				const fetchDssiCodesArgs: fetchDssiCodesForCustomerAndSupplierArgs = {
					customerId,
					supplierId: supplier.id,
				};
				const dssiCodesResult = await dispatch(
					fetchDssiCodesForCustomerAndSupplier(fetchDssiCodesArgs)
				);
				if (
					fetchDssiCodesForCustomerAndSupplier.fulfilled.match(dssiCodesResult)
				) {
					dispatch(
						reportModalSlice.actions.setSupplierDistributionCenterData({
							supplierId: supplier.id,
							dcCodes: dssiCodesResult.payload,
						})
					);
					distributionCenterCodes = distributionCenterCodes.concat(
						dssiCodesResult.payload
					);
				} else {
					rejectWithValue(dssiCodesResult.payload);
				}
			} else {
				distributionCenterCodes = distributionCenterCodes.concat(
					supplier.distributionCenterCodes
				);
			}
		}

		const distributionCenters = selectDistributionCentersByDssiCodes(
			state,
			distributionCenterCodes
		).filter((item) => item !== undefined && item !== null);
		const missingDssiCodes = lodash.difference(
			distributionCenterCodes,
			distributionCenters.map((item) => item.dssiCode)
		);

		if (missingDssiCodes.length > 0) {
			const getDCsArgs: GetDistributionCentersByDssiCodesArgs = {
				distributionCenterDssiCodes: missingDssiCodes,
			};
			const distributionCentersResult = await dispatch(
				fetchDistributionCentersForDssiCodes(getDCsArgs)
			);
			if (
				fetchDistributionCentersForDssiCodes.fulfilled.match(
					distributionCentersResult
				)
			) {
				distributionCenters.push(...distributionCentersResult.payload);
			} else {
				rejectWithValue(distributionCentersResult.payload);
			}
		}

		const orderedDistributionCenters = lodash.sortBy(
			distributionCenters,
			(dc) => dc.name.toLowerCase()
		);

		dispatch(
			reportModalSlice.actions.setDistributionCentersData({
				distributionCenters: orderedDistributionCenters,
				distributionCenterCodes,
			})
		);
	}
);

export const fetchMasterOrderGuides = createAsyncThunk<
	void,
	SupplierInfo[],
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportModalSlice/fetchMasterOrderGuides',
	async (
		suppliers: SupplierInfo[],
		{ getState, dispatch, rejectWithValue }
	) => {
		const state = getState().reportsPage.reportRequestModalState;

		const requestArgs: SelectBlueprintsDataMultSuppliersArgs = {
			supplierIds: suppliers.map((x) => x.id),
			customerId: state.customerId,
		};

		try {
			const blueprintsResponse = await unwrapFetch2(
				ProductController.SelectBlueprintsDataMultSuppliers(requestArgs)
			);
			const orderedMasterOrderGuides = lodash.sortBy(
				blueprintsResponse.masterOrderGuides,
				(mog) => mog.name.toLowerCase()
			);

			dispatch(
				reportModalSlice.actions.setMasterOrderGuidesData(
					orderedMasterOrderGuides
				)
			);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const fetchBlueprintDcLinks = createAsyncThunk<
	void,
	SupplierInfo[],
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportModalSlice/fetchBlueprintDcLinks',
	async (
		suppliers: SupplierInfo[],
		{ getState, dispatch, rejectWithValue }
	) => {
		const state = getState();
		const customerId = state.reportsPage.reportRequestModalState.customerId;
		let blueprintDcLinks: BlueprintDistributionCenterLink[] = [];

		const fetchBlueprintDcLinksArgs: FetchBlueprintDcLinksForCustomerAndSuppliersArgs =
			{
				customerId,
				supplierIds: suppliers.map((s) => s.id),
			};
		const blueprintDcLinksResult = await dispatch(
			fetchBlueprintDcLinksForCustomerAndSupplier(fetchBlueprintDcLinksArgs)
		);
		if (
			fetchBlueprintDcLinksForCustomerAndSupplier.fulfilled.match(
				blueprintDcLinksResult
			)
		) {
			blueprintDcLinks = blueprintDcLinksResult.payload;
		} else {
			rejectWithValue(blueprintDcLinksResult.payload);
		}

		dispatch(
			reportModalSlice.actions.setBlueprintDistributionCenterLinkData({
				blueprintDcLinks,
			})
		);
	}
);

export const fetchLocations = createAsyncThunk<
	void,
	SupplierInfo[],
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportModalSlice/fetchLocations',
	async (
		suppliers: SupplierInfo[],
		{ getState, dispatch, rejectWithValue }
	) => {
		const state = getState().reportsPage.reportRequestModalState;

		try {
			const locationsResponse = await unwrapFetch2(
				LocationDistributionCenterController.GetLocationsForCustomerAndMultipleSuppliers(
					{
						customerId: state.customerId,
						supplierIds: suppliers.map((x) => x.id),
					}
				)
			);
			const orderedLocations = lodash.sortBy(
				locationsResponse.locations,
				(loc) => loc.name.toLowerCase()
			);

			dispatch(reportModalSlice.actions.setLocationsData(orderedLocations));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const submitOrderGuideDownloadRequest = createAsyncThunk<
	ReportRequestRow,
	null,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportModalSlice/submitOrderGuideDownloadRequest',
	async (_, { getState, rejectWithValue }) => {
		const state = getState();
		const {
			customerId,
			selections: {
				suppliers,
				distributionCenters,
				masterOrderGuides,
				locations,
				splitReportByDc,
			},
		} = state.reportsPage.reportRequestModalState;

		const OGReportRequestArgs: RequestOrderGuideReportArgs = {
			customerId,
			supplierId: suppliers[0].id,
			locationIds: locations ? locations.map((loc) => loc.id) : [],
			distributionCenterIds: distributionCenters
				? distributionCenters.map((dc) => dc.id)
				: [],
			masterOrderGuideIds: masterOrderGuides
				? masterOrderGuides.map((mog) => mog.id)
				: [],
			splitReportByDc,
		};

		const response = await unwrapFetch2(
			ReportController.RequestOrderGuideReport(OGReportRequestArgs)
		);
		if (response.errors != null && response.errors.length > 0) {
			rejectWithValue(response.errors);
		}

		return response.reportRequestRow;
	}
);

export const submitStockMatrixRequest = createAsyncThunk<
	ReportRequestRow,
	null,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportModalSlice/submitStockMatrixRequest',
	async (_, { getState, rejectWithValue }) => {
		const state = getState();
		const {
			customerId,
			selections: {
				suppliers,
				distributionCenters,
				masterOrderGuides,
				locations,
			},
		} = state.reportsPage.reportRequestModalState;

		const StockMatrixReportRequestArgs: RequestStockMatrixReportArgs = {
			customerId,
			supplierIds: suppliers ? suppliers.map((s) => s.id) : [],
			locationIds: locations ? locations.map((loc) => loc.id) : [],
			distributionCenterIds: distributionCenters
				? distributionCenters.map((dc) => dc.id)
				: [],
			masterOrderGuideIds: masterOrderGuides
				? masterOrderGuides.map((mog) => mog.id)
				: [],
		};

		const reponse = await unwrapFetch2(
			ReportController.RequestStockMatrixReport(StockMatrixReportRequestArgs)
		);
		if (reponse.errors != null && reponse.errors.length > 0) {
			rejectWithValue(reponse.errors);
		}

		return reponse.reportRequestRow;
	}
);

export const reportModalSlice = createSlice({
	name: 'reportModalSlice',
	initialState,
	reducers: {
		resetModal: (state) => {
			state.loadingStates = initialState.loadingStates;
			state.selections = initialState.selections;
			state.data.masterOrderGuides = initialState.data.masterOrderGuides;
			state.data.distributionCenters = initialState.data.distributionCenters;
			state.data.locations = initialState.data.locations;
			state.data.blueprintDcLinks = initialState.data.blueprintDcLinks;
			state.filteredData.masterOrderGuides =
				initialState.filteredData.masterOrderGuides;
			state.filteredData.distributionCenters =
				initialState.filteredData.distributionCenters;
		},
		setCurrentCustomerId: (state, action) => {
			state.customerId = action.payload;
		},
		setSupplierData: (state, action) => {
			state.data.suppliers = action.payload;
		},
		setMasterOrderGuidesData: (state, action) => {
			state.data.masterOrderGuides = action.payload;
			state.filteredData.masterOrderGuides = action.payload;
		},
		setLocationsData: (state, action) => {
			state.selections.locations = initialState.selections.locations;
			state.data.locations = action.payload;
		},
		setSupplierDistributionCenterData: (state, action) => {
			const supplierIndex = state.data.suppliers.findIndex(
				(sup) => sup.id === action.payload.supplierId
			);
			if (supplierIndex >= 0) {
				state.data.suppliers[supplierIndex].distributionCenterCodes =
					action.payload.dcCodes;
			}
		},
		setDistributionCentersData: (state, action) => {
			state.data.distributionCenters = action.payload.distributionCenters;
			state.filteredData.distributionCenters =
				action.payload.distributionCenters;
		},
		setSupplier: (state, action) => {
			state.selections.suppliers = action.payload;
		},
		setBlueprintDistributionCenterLinkData: (state, action) => {
			state.data.blueprintDcLinks = action.payload.blueprintDcLinks;
		},
		updateSelectedMasterOrderGuides: (state, action) => {
			state.selections.masterOrderGuides = action.payload;
		},
		updateSelectedDistributionCenters: (state, action) => {
			state.selections.distributionCenters = action.payload;
		},
		updateSelectedLocations: (state, action) => {
			state.selections.locations = action.payload;
		},
		updateSplitByDcFilter: (state) => {
			state.selections.splitReportByDc = !state.selections.splitReportByDc;
		},
		acknowledgeFetchError: (state) => {
			state.errors.fetchDataError = false;
		},
		filterMasterOrderGuides: (state) => {
			if (
				state.selections.distributionCenters === null ||
				state.selections.distributionCenters.length === 0
			) {
				state.filteredData.masterOrderGuides = state.data.masterOrderGuides;
			} else {
				const dcIds = state.selections.distributionCenters.map((dc) => dc.id);
				const filteredBlueprintIds = state.data.blueprintDcLinks
					.filter((x) => dcIds.includes(x.distributionCenterId))
					.map((x) => x.blueprintId);
				state.filteredData.masterOrderGuides =
					state.data.masterOrderGuides.filter((bp) =>
						filteredBlueprintIds.includes(bp.id)
					);
				if (state.selections.masterOrderGuides !== null) {
					state.selections.masterOrderGuides =
						state.selections.masterOrderGuides.filter((bp) =>
							filteredBlueprintIds.includes(bp.id)
						);
				}
			}
		},
		filterDistributionCenters: (state) => {
			if (
				state.selections.masterOrderGuides === null ||
				state.selections.masterOrderGuides.length === 0
			) {
				state.filteredData.distributionCenters = state.data.distributionCenters;
			} else {
				const blueprintIds = state.selections.masterOrderGuides.map(
					(bp) => bp.id
				);
				const filteredDcIds = state.data.blueprintDcLinks
					.filter((x) => blueprintIds.includes(x.blueprintId))
					.map((x) => x.distributionCenterId);
				state.filteredData.distributionCenters =
					state.data.distributionCenters.filter((dc) =>
						filteredDcIds.includes(dc.id)
					);
				if (state.selections.distributionCenters !== null) {
					state.selections.distributionCenters =
						state.selections.distributionCenters.filter((dc) =>
							filteredDcIds.includes(dc.id)
						);
				}
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(submitOrderGuideDownloadRequest.pending, (state) => {
			state.loadingStates.submittingRequest = true;
		});
		builder.addCase(submitOrderGuideDownloadRequest.fulfilled, (state) => {
			state.loadingStates = initialState.loadingStates;
			state.selections = initialState.selections;
			state.data.masterOrderGuides = initialState.data.masterOrderGuides;
			state.data.distributionCenters = initialState.data.distributionCenters;
		});
		builder.addCase(submitOrderGuideDownloadRequest.rejected, (state) => {
			state.errors.requestReportError = true;
			state.loadingStates.submittingRequest = false;
		});
		builder.addCase(submitStockMatrixRequest.pending, (state) => {
			state.loadingStates.submittingRequest = true;
		});
		builder.addCase(submitStockMatrixRequest.fulfilled, (state) => {
			state.loadingStates = initialState.loadingStates;
			state.selections = initialState.selections;
			state.data.masterOrderGuides = initialState.data.masterOrderGuides;
			state.data.distributionCenters = initialState.data.distributionCenters;
		});
		builder.addCase(submitStockMatrixRequest.rejected, (state) => {
			state.errors.requestReportError = true;
			state.loadingStates.submittingRequest = false;
		});
		builder.addCase(loadModalForCustomer.pending, (state) => {
			state.selections.suppliers = initialState.selections.suppliers;
			state.loadingStates.loadingSuppliers = true;
		});
		builder.addCase(loadModalForCustomer.fulfilled, (state) => {
			state.loadingStates.loadingSuppliers = false;
		});
		builder.addCase(loadModalForCustomer.rejected, (state) => {
			state.loadingStates.loadingSuppliers = false;
			state.errors.fetchDataError = true;
		});
		builder.addCase(fetchDistributionCenters.pending, (state) => {
			state.selections.distributionCenters =
				initialState.selections.distributionCenters;
			state.loadingStates.loadingDistributionCenters = true;
		});
		builder.addCase(fetchDistributionCenters.fulfilled, (state) => {
			state.loadingStates.loadingDistributionCenters = false;
		});
		builder.addCase(fetchDistributionCenters.rejected, (state) => {
			state.loadingStates.loadingDistributionCenters = false;
			state.errors.fetchDataError = true;
		});
		builder.addCase(fetchMasterOrderGuides.pending, (state) => {
			state.selections.masterOrderGuides =
				initialState.selections.masterOrderGuides;
			state.loadingStates.loadingMasterOrderGuides = true;
		});
		builder.addCase(fetchMasterOrderGuides.fulfilled, (state) => {
			state.loadingStates.loadingMasterOrderGuides = false;
		});
		builder.addCase(fetchMasterOrderGuides.rejected, (state) => {
			state.loadingStates.loadingMasterOrderGuides = false;
			state.errors.fetchDataError = true;
		});
		builder.addCase(fetchBlueprintDcLinks.pending, (state) => {
			state.loadingStates.loadingBlueprintDcLinks = true;
		});
		builder.addCase(fetchBlueprintDcLinks.fulfilled, (state) => {
			state.loadingStates.loadingBlueprintDcLinks = false;
		});
		builder.addCase(fetchBlueprintDcLinks.rejected, (state) => {
			state.loadingStates.loadingBlueprintDcLinks = false;
			state.errors.fetchDataError = true;
		});
	},
});

export const {
	resetModal,
	setSupplier,
	updateSelectedMasterOrderGuides,
	updateSelectedDistributionCenters,
	updateSelectedLocations,
	updateSplitByDcFilter,
	acknowledgeFetchError,
	filterMasterOrderGuides,
	filterDistributionCenters,
} = reportModalSlice.actions;
